/**
 * Demo Enquiry — for preschool owners / franchise networks who want
 * to evaluate MangoCubs as their operating platform.
 *
 * Deliberately B2B-coded: dark hero, structured fields, no warm preschool
 * imagery — so it's instantly distinguishable from the public Admission
 * Enquiry form that parents fill out.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle, PaperPlaneTilt, Buildings, Briefcase, Globe, ArrowRight,
  Package, ChartLineUp,
} from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import PhoneInput from "@/components/PhoneInput";
import api, { formatApiErrorDetail } from "@/lib/api";

const MODULES = [
  "Admissions & CRM", "Fees & Finance", "Attendance & Pickup",
  "LMS & Curriculum", "HRMS & Payroll", "Parent App",
  "Documents & Compliance", "Activity / Daycare", "WhatsApp + SMS",
];
const SLOTS = [
  "Today afternoon", "Tomorrow morning", "Tomorrow afternoon",
  "This weekend", "Weekday morning", "Weekday afternoon",
  "Weekday evening", "Anytime — surprise me",
];

export default function DemoEnquiry() {
  const [form, setForm] = useState({
    school_name: "", contact_name: "", designation: "",
    phone: "", email: "",
    number_of_branches: 1,
    city: "", country: "India",
    interested_modules: [],
    preferred_demo_time: "Weekday afternoon",
    message: "",
  });
  const [phoneValid, setPhoneValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [err, setErr] = useState("");

  const toggleModule = (m) => setForm((f) => ({
    ...f,
    interested_modules: f.interested_modules.includes(m)
      ? f.interested_modules.filter((x) => x !== m)
      : [...f.interested_modules, m],
  }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone || !phoneValid) { setErr("Please enter a valid mobile with country code."); return; }
    if (!form.email.includes("@")) { setErr("Please enter a valid work email."); return; }
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/public/demo-enquiry", form);
      setSubmitted(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0E0F12] text-white flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center bg-white/5 backdrop-blur rounded-3xl border border-white/10 p-10"
          data-testid="demo-enquiry-thanks">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400">
            <CheckCircle size={36} weight="duotone" />
          </div>
          <h2 className="font-heading text-3xl font-bold mt-4">Thanks, {submitted.contact_name.split(" ")[0]}!</h2>
          <p className="text-white/70 mt-2">
            We've received your demo request for <span className="font-semibold text-white">{submitted.school_name}</span>.
            Our team will reach out at <span className="font-semibold text-white">{submitted.phone}</span> within 24 hours.
          </p>
          <div className="mt-5 inline-block bg-emerald-500/10 border border-emerald-500/20 px-4 py-3 rounded-2xl text-sm font-mono text-emerald-200" data-testid="demo-id">
            {submitted.demo_id}
          </div>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:bg-white/5 text-sm">
            Back to MangoCubs <ArrowRight weight="bold" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E0F12] text-white" data-testid="demo-enquiry-page">
      {/* Hero band */}
      <div className="px-6 lg:px-12 py-6 border-b border-white/10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5"><Logo size={40} tagline={false} /></Link>
        <a href="mailto:info@factskoot.com" className="text-xs text-white/60 hover:text-white">info@factskoot.com</a>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12 grid lg:grid-cols-5 gap-10">
        {/* Left — pitch */}
        <div className="lg:col-span-2 space-y-6">
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-2.5 py-1 rounded-full">
            <Package weight="duotone" size={14} /> For preschool operators
          </span>
          <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">
            Book a MangoCubs<sup className="text-base">™</sup> demo
          </h1>
          <p className="text-white/70 text-lg leading-relaxed">
            Explore MangoCubs Preschool OS for your preschool, daycare or franchise network. One pricing, every module, multi-branch ready.
          </p>
          <ul className="space-y-3 text-sm text-white/80">
            <Feature icon={Buildings} title="Multi-branch & franchise-ready" body="Strict tenant isolation, branded sub-domains, role-based access." />
            <Feature icon={ChartLineUp} title="Conversion-focused CRM" body="Enquiry funnel, counsellor leaderboards, source attribution, follow-up board." />
            <Feature icon={Briefcase} title="One platform for everything" body="LMS, Fees, HRMS, Payroll, Documents, Parent app — no toolchain stitching." />
          </ul>
          <div className="text-xs text-white/40 pt-4 border-t border-white/10">
            Trusted by preschool networks across India · ISO-aligned data privacy
          </div>
        </div>

        {/* Right — form */}
        <form onSubmit={onSubmit} className="lg:col-span-3 bg-white/5 backdrop-blur rounded-3xl border border-white/10 p-6 lg:p-8 space-y-4"
              data-testid="demo-enquiry-form">
          <h2 className="font-heading text-2xl font-semibold mb-1">Tell us about your school</h2>
          <p className="text-sm text-white/60 mb-4">Takes under 90 seconds. No credit card.</p>

          <div className="grid sm:grid-cols-2 gap-3">
            <DInput label="School / Franchise name *" required
              value={form.school_name} onChange={(v) => setForm({ ...form, school_name: v })}
              placeholder="e.g. Little Sprouts Preschool"
              testid="demo-school" />
            <DInput label="Your name *" required
              value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })}
              placeholder="e.g. Priya Sharma"
              testid="demo-contact" />
            <DInput label="Designation / Role"
              value={form.designation} onChange={(v) => setForm({ ...form, designation: v })}
              placeholder="Owner, Director, Principal, Operations Head…"
              testid="demo-designation" />
            <div>
              <div className="text-xs text-white/60 mb-1.5">Mobile (with country code) *</div>
              <PhoneInput
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                onValidityChange={setPhoneValid}
                dark
                placeholder="9844139333"
                testId="demo-phone"
              />
            </div>
            <DInput label="Work email *" required type="email"
              value={form.email} onChange={(v) => setForm({ ...form, email: v })}
              placeholder="priya@littlesprouts.com"
              testid="demo-email" className="sm:col-span-2" />
            <DInput label="Number of branches" type="number"
              value={form.number_of_branches} onChange={(v) => setForm({ ...form, number_of_branches: parseInt(v, 10) || 1 })}
              placeholder="e.g. 3"
              testid="demo-branches" />
            <DInput label="City"
              value={form.city} onChange={(v) => setForm({ ...form, city: v })}
              placeholder="Bangalore, Singapore, Dubai…"
              testid="demo-city" />
            <DInput label="Country"
              value={form.country} onChange={(v) => setForm({ ...form, country: v })}
              placeholder="India"
              testid="demo-country" />
            <div>
              <div className="text-xs text-white/60 mb-1.5">Preferred demo time</div>
              <select className="dinput"
                value={form.preferred_demo_time}
                onChange={(e) => setForm({ ...form, preferred_demo_time: e.target.value })}
                data-testid="demo-slot">
                {SLOTS.map((s) => <option key={s} value={s} className="bg-[#0E0F12]">{s}</option>)}
              </select>
            </div>
          </div>

          <div className="pt-2">
            <div className="text-xs text-white/60 mb-2">Modules you're interested in (pick any)</div>
            <div className="flex flex-wrap gap-1.5" data-testid="demo-modules">
              {MODULES.map((m) => {
                const on = form.interested_modules.includes(m);
                return (
                  <button type="button" key={m}
                    onClick={() => toggleModule(m)}
                    data-testid={`demo-mod-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      on ? "bg-emerald-500 text-[#0E0F12] border-emerald-500" : "bg-white/5 text-white/80 border-white/15 hover:bg-white/10"
                    }`}>
                    {m}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs text-white/60 mb-1.5">Anything specific we should cover?</div>
            <textarea className="dinput min-h-[88px]" rows={3}
              data-testid="demo-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="e.g. We're moving from spreadsheets, struggling with multi-branch fees…" />
          </div>

          {err && <div className="text-sm text-rose-400" data-testid="demo-error">{err}</div>}

          <button type="submit" disabled={loading}
            data-testid="demo-submit-btn"
            className="w-full h-12 rounded-full bg-emerald-500 text-[#0E0F12] font-semibold flex items-center justify-center gap-2 hover:bg-emerald-400 transition disabled:opacity-60">
            {loading ? "Submitting…" : (<>Book my demo <PaperPlaneTilt weight="bold" /></>)}
          </button>

          <p className="text-[11px] text-white/40 text-center">
            By submitting, you agree we may reach out via WhatsApp / call / email. No marketing spam.
          </p>
        </form>
      </div>

      <style>{`
        .dinput {
          width: 100%; height: 44px; padding: 0 14px; border-radius: 10px;
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12);
          color: #fff; font-family: 'DM Sans', sans-serif; font-size: 14px;
        }
        .dinput::placeholder { color: rgba(255,255,255,0.35); }
        .dinput:focus { outline: none; border-color: rgba(52,211,153,0.6); box-shadow: 0 0 0 2px rgba(52,211,153,0.25); }
        textarea.dinput { padding: 12px 14px; height: auto; }
      `}</style>
    </div>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-emerald-400">
        <Icon weight="duotone" size={18} />
      </span>
      <div>
        <div className="font-semibold text-white">{title}</div>
        <div className="text-white/60 text-[13px]">{body}</div>
      </div>
    </li>
  );
}

function DInput({ label, required, type = "text", value, onChange, placeholder, testid, className = "" }) {
  return (
    <div className={className}>
      <div className="text-xs text-white/60 mb-1.5">{label}</div>
      <input className="dinput" type={type} required={required}
        data-testid={testid}
        value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
